import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, Phone, ArrowRight, Scale, Paintbrush, Shield, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AboutPage() {
  const [team, setTeam] = useState([]);

  useEffect(() => {
    axios.get(`${API}/team`).then((r) => {
      const filtered = r.data.filter(
        (m) => m.name.toLowerCase() !== "sarah sefiji"
      );
      setTeam(filtered);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      {/* Hero with parallax */}
      <div className="relative overflow-hidden">
        <img
          src="https://customer-assets-4nw71qhi.emergentagent.net/job_c328fdc4-934d-4a64-9757-bf94f6ebfb76/artifacts/091aaff4e570de15_23817x4851%20%28116%20Mpx%29-2.webp"
          alt="Genève"
          className="w-full h-auto block"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-6 lg:px-12 pb-12 lg:pb-16">
          <div className="max-w-7xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#699ab6] font-medium mb-3">
              L'agence
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-white tracking-tight">
              Qui sommes-nous ?
            </h1>
          </div>
        </div>
      </div>

      {/* Story */}
      <section className="py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
            <div className="lg:sticky lg:top-28">
              <h2 className="font-heading text-3xl lg:text-4xl font-medium text-[#333333] tracking-tight mb-6">
                Une agence née
                <br />
                de la passion
              </h2>
              <div className="w-16 h-0.5 bg-[#699ab6] mb-8" />
              <p className="font-accent italic text-[#699ab6] text-lg leading-relaxed">
                "Nous croyons que chaque projet immobilier est unique et mérite un accompagnement sur mesure."
              </p>
            </div>

            <div className="space-y-6">
              <p className="text-[#777777] text-base leading-relaxed font-light">
                Née de l'union entre un juriste, titulaire du brevet d'avocat et d'un spécialiste
                en communication et arts graphiques, ces deux entrepreneurs ont décidé de mettre
                leurs compétences respectives en commun afin de fonder Vingt-Neuf Immobilier.
              </p>
              <p className="text-[#777777] text-base leading-relaxed font-light">
                Nous sommes une agence immobilière dynamique et passionnée, spécialisée dans la
                vente de biens résidentiels et commerciaux et le développement immobilier.
              </p>
              <p className="text-[#777777] text-base leading-relaxed font-light">
                Forts d'une équipe expérimentée et dévouée, nous mettons tout en oeuvre pour
                répondre aux besoins uniques de nos clients. Notre approche personnalisée, notre
                transparence et notre engagement inébranlable garantissent une expérience
                immobilière sans stress et pleinement satisfaisante.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F5F5F5] py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#699ab6] font-medium mb-3">
              Nos valeurs
            </p>
            <h2 className="font-heading text-3xl lg:text-4xl font-medium text-[#333333] tracking-tight">
              Ce qui nous distingue
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Scale, title: "Expertise juridique", desc: "Un accompagnement juridique complet pour sécuriser vos transactions immobilières à chaque étape." },
              { icon: Paintbrush, title: "Vision créative", desc: "Une présentation soignée et innovante de chaque bien pour le mettre en valeur et séduire les acquéreurs." },
              { icon: Shield, title: "Transparence", desc: "Une communication claire et honnête, sans surprises. Vous êtes informés en temps réel de l'avancement." },
              { icon: Users, title: "Sur mesure", desc: "Un service entièrement adapté à vos besoins, votre projet et vos contraintes. Chaque client est unique." },
            ].map((value) => (
              <div key={value.title} className="bg-white p-8 border border-[#E5E5E5] group hover:border-[#699ab6] transition-colors">
                <value.icon className="w-7 h-7 text-[#699ab6] mb-5" />
                <h3 className="font-heading text-lg font-medium text-[#333333] mb-3">
                  {value.title}
                </h3>
                <p className="text-sm text-[#777] font-light leading-relaxed">
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section data-testid="team-section" className="py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#699ab6] font-medium mb-3">
              Notre équipe
            </p>
            <h2 className="font-heading text-3xl lg:text-4xl font-medium text-[#333333] tracking-tight">
              Les visages de Vingt-Neuf
            </h2>
          </div>

          <div className={`grid grid-cols-1 ${team.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-3'} gap-10 lg:gap-12`}>
            {team.map((member) => (
              <div
                key={member.id}
                data-testid={`team-member-${member.id}`}
                className="group text-center"
              >
                <div className="relative overflow-hidden aspect-[3/4] mb-6 bg-white">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-heading text-xl font-medium text-[#333333] mb-1">
                  {member.name}
                </h3>
                <p className="font-accent italic text-[#699ab6] text-sm mb-4">
                  {member.role}
                </p>
                <p className="text-sm text-[#777] font-light leading-relaxed mb-4 max-w-xs mx-auto">
                  {member.bio}
                </p>
                <div className="flex items-center justify-center gap-4">
                  <a
                    href={`mailto:${member.email}`}
                    className="text-[#777] hover:text-[#699ab6] transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                  </a>
                  <a
                    href={`tel:${member.phone?.replace(/\s/g, "")}`}
                    className="text-[#777] hover:text-[#699ab6] transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 px-6 lg:px-12 bg-[#333333]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-accent italic text-[#699ab6] text-lg mb-4">
            Faites-nous confiance
          </p>
          <h2 className="font-heading text-3xl lg:text-4xl font-medium text-white tracking-tight mb-8">
            Réalisons ensemble votre projet immobilier
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="inline-flex items-center gap-3 bg-[#699ab6] text-white px-10 py-4 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#5a8ba5] transition-all"
          >
            Nous contacter
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
